import { ApiClient } from './client';
import { inferContentType, materialsApi, SignedUploadError, uploadFileToSignedUrl, type UserMaterial } from './materials';
import { hashUploadFile } from './upload-content-hash';
import type { referenceEditPayload } from '@/lib/reference-video-edit';

export interface ReferenceUploadReceipt {
  storage_path: string; file_name: string; content_type: string; file_size: number; content_sha256: string;
}
export interface ReferenceSourceReceipt { id: string; upload?: ReferenceUploadReceipt }
class ReferenceVideoEditApi extends ApiClient {
  /** Direct PUT is retained for large files; verification and editing share one server read. */
  async upload(file: File, onProgress: (value: number) => void, signal: AbortSignal): Promise<ReferenceSourceReceipt> {
    const content_type = inferContentType(file);
    if (!content_type.startsWith('video/') || file.size > 200 * 1024 * 1024) throw new Error('invalid_reference_video');
    onProgress(0);
    const content_sha256 = await hashUploadFile(file, signal);
    const result = await materialsApi.presignUpload({ file_name: file.name, content_type, file_size: file.size,
      material_type: 'general', content_sha256, asset_category: 'user_material' }, signal);
    if (!result.success || !result.data) throw new Error('reference_presign_failed');
    if (result.data.duplicate) return { id: result.data.asset_id };
    try {
      await uploadFileToSignedUrl(result.data.upload_url, file, content_type, 1, value => onProgress(value.percentage), signal);
    } catch (error) {
      // A lost PUT acknowledgement is resolved by server digest verification, not a second PUT.
      if (!(error instanceof SignedUploadError) || !['upload_timeout', 'upload_network'].includes(error.code) || signal.aborted) throw error;
    }
    signal.throwIfAborted();
    return { id: result.data.asset_id, upload: { storage_path: result.data.storage_path,
      file_name: file.name, content_type, file_size: file.size, content_sha256 } };
  }
  edit(payload: ReturnType<typeof referenceEditPayload> & { upload?: ReferenceUploadReceipt }, signal?: AbortSignal) {
    return this.request<UserMaterial>('/materials/edit-reference-video', { method: 'POST', signal, body: JSON.stringify(payload) });
  }
}
export const referenceVideoEditApi = new ReferenceVideoEditApi();
